import React, { useState, useEffect } from "react";
import { axiosInstance } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Display labels for menu keys (kept here so the UI is self-contained).
const MENU_LABELS = {
  analytics: "Analytics Dashboard",
  user_mgmt_users: "User Management",
  user_mgmt_websites: "Website Management",
  user_mgmt_roles: "Role Management",
  export_logs: "Log Export Download",
  member_history: "Member History Log",
  input_member: "Input Member",
  marketing_perf: "Performance Report",
  follow_up_panel: "Follow Up Member",
  todo_list: "Submit Member",
  member_report: "Member Report",
  performance_review: "Performance Review",
};

export const RolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [defaultMenusPerBase, setDefaultMenusPerBase] = useState({});
  const [loading, setLoading] = useState(false);

  const [roleForm, setRoleForm] = useState({
    name: "",
    base_role: "marketing",
    description: "",
    accessible_menus: [],
  });
  const [editingRole, setEditingRole] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchAvailableMenus();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get("/roles");
      setRoles(response.data);
    } catch (error) {
      toast.error("Gagal memuat data role");
    }
  };

  const fetchAvailableMenus = async () => {
    try {
      const response = await axiosInstance.get("/menus/available");
      setAvailableMenus(response.data.available || []);
      setDefaultMenusPerBase(response.data.defaults_per_base_role || {});
    } catch (error) {
      // Non-fatal: dialog will simply show no checkboxes.
      console.error("Failed to fetch available menus", error);
    }
  };

  const toggleMenuInForm = (menuKey) => {
    setRoleForm((prev) => {
      const cur = prev.accessible_menus || [];
      return {
        ...prev,
        accessible_menus: cur.includes(menuKey)
          ? cur.filter((m) => m !== menuKey)
          : [...cur, menuKey],
      };
    });
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingRole) {
        await axiosInstance.put(`/roles/${editingRole.id}`, roleForm);
        toast.success("Role berhasil diupdate");
      } else {
        await axiosInstance.post("/roles", roleForm);
        toast.success("Role berhasil ditambahkan");
      }
      setShowRoleDialog(false);
      setRoleForm({ name: "", base_role: "marketing", description: "", accessible_menus: [] });
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan role");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm("Yakin ingin menghapus role ini?")) return;
    try {
      await axiosInstance.delete(`/roles/${id}`);
      toast.success("Role berhasil dihapus");
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menghapus role");
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      base_role: role.base_role,
      description: role.description || "",
      accessible_menus: role.accessible_menus || [],
    });
    setShowRoleDialog(true);
  };

  return (
    <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30" data-testid="roles-tab-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-red-50">Manajemen Role</CardTitle>
            <CardDescription className="text-gray-400">
              Atur menu apa saja yang dapat diakses oleh setiap role.
            </CardDescription>
          </div>
          <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingRole(null);
                  setRoleForm({
                    name: "",
                    base_role: "marketing",
                    description: "",
                    accessible_menus: defaultMenusPerBase["marketing"] || [],
                  });
                }}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                data-testid="add-role-button"
              >
                <Plus className="mr-2" size={18} />
                Tambah Role
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-red-900/30">
              <DialogHeader>
                <DialogTitle className="text-red-50">
                  {editingRole ? "Edit Role" : "Tambah Role Baru"}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  {editingRole ? "Atur menu yang dapat diakses role ini" : "Tentukan nama role dan menu yang dapat diakses"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleRoleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="role-name" className="text-gray-300">Nama Role</Label>
                  <Input
                    id="role-name"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    required
                    placeholder="Contoh: Admin Marketing, Senior FU"
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role-desc" className="text-gray-300">Deskripsi (opsional)</Label>
                  <Input
                    id="role-desc"
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    placeholder="Deskripsi role"
                    className="bg-[#0f0f10] border-red-900/30 text-white"
                    data-testid="role-desc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Menu yang Dapat Diakses</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 bg-[#0f0f10] border border-red-900/30 rounded-md">
                    {availableMenus.map((menuKey) => (
                      <label
                        key={menuKey}
                        className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer hover:text-white"
                      >
                        <Checkbox
                          checked={(roleForm.accessible_menus || []).includes(menuKey)}
                          onCheckedChange={() => toggleMenuInForm(menuKey)}
                          data-testid={`role-menu-${menuKey}`}
                        />
                        <span>{MENU_LABELS[menuKey] || menuKey}</span>
                      </label>
                    ))}
                    {availableMenus.length === 0 && (
                      <div className="text-sm text-gray-500">Tidak ada menu tersedia</div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    User dengan role ini hanya akan melihat menu yang dicentang di sidebar.
                    Dashboard selalu tersedia.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                  data-testid="save-role-button"
                >
                  {loading ? "Menyimpan..." : (editingRole ? "Update Role" : "Tambah Role")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {roles.length === 0 && (
            <div className="text-center py-8 text-gray-400" data-testid="roles-empty">
              Belum ada role custom. Klik "Tambah Role" untuk membuat role baru.
            </div>
          )}
          {roles.map((role) => (
            <div
              key={role.id}
              className="flex items-center justify-between p-4 bg-[#0f0f10] rounded-lg border border-red-900/20"
              data-testid={`role-item-${role.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-semibold text-white">{role.name}</div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-700/40">
                    {(role.accessible_menus || []).length} menu
                  </span>
                </div>
                {role.description && (
                  <div className="text-sm text-gray-400 mt-1 truncate">{role.description}</div>
                )}
              </div>
              <div className="flex gap-2 ml-3">
                <Button
                  onClick={() => openEditRole(role)}
                  variant="ghost"
                  size="sm"
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                  data-testid={`edit-role-${role.id}`}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  onClick={() => handleDeleteRole(role.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                  data-testid={`delete-role-${role.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RolesTab;
